import * as rd from '@devexperts/remote-data-ts'
import { BookingPeriod, DatePicker } from '@spiaggeit/spit-datepicker'
import { cn } from '@spiaggeit/spit-ui'
import { DateTime } from 'luxon'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { generatePath, useNavigate, useSearchParams } from 'react-router-dom'

import { KioskTimeoutDialog } from '@/components/Dialog/KioskTimeoutDialog'
import { LoaderTrigger } from '@/components/LoaderTrigger'
import { SectorRulesDialog } from '@/components/sectorRulesDialog'
import { PreemptionCode } from '@/components/sectorRulesDialog/PreemptionCode'
import { PreemptionCodeErrorDialog } from '@/components/sectorRulesDialog/PreemptionCodeErrorDialog'
import { ROOT_PATH } from '@/app/router/paths'
import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { useIsInIFrame } from '@/hooks/useIsInIFrame'
import { useLoadUnavailableSectors } from '@/hooks/useLoadUnavailableSectors'
import { useLockDates } from '@/hooks/useLockDates'
import { usePeriod } from '@/hooks/usePeriod'
import { AppMode } from '@/models/app'
import { SectorRuleError } from '@/models/http'
import { appSlice, isKioskSelector } from '@/store/appSlice'
import { licenseSlice } from '@/store/licenseSlice'
import { servicesSlice } from '@/store/servicesSlice'
import { resetSessionStorageData } from '@/store/sessionStorageMiddleware'
import { ONE_MINUTE } from '@/utils/constants'
import { toJSDateWithTimezone } from '@/utils/dateUtils'
import { getNextPathAfterChoosingPeriod } from '@/utils/getNextPathAfterChoosingPeriod'
import {
  HALF_DAY_QUERY_PARAM,
  getHalfDayValueFromBookingPeriod,
} from '@/utils/halfDay'
import {
  BookingAvailability,
  bookingAvailabilitySlice,
} from '../../store/bookingAvailabilitySlice'
import {
  bookingPeriodSelector,
  insertPeriodSlice,
  periodAsUnixTimeSelector,
  PeriodSliceState,
} from '../../store/insertPeriodSlice'
import { sectorRulesSlice } from '../../store/sectorRulesSlice'
import { getShouldValidateForm } from '../../utils/sectorRulesUtils'

import { InsertPeriodAlerts } from './Alerts'

export function InsertPeriod() {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const rawLockDates = useLockDates()
  const { t, i18n } = useTranslation()
  const [searchParams] = useSearchParams()
  const isInIFrame = useIsInIFrame()

  const lockDates = {
    maxDate: new Date(rawLockDates.maxDate),
    minDate: new Date(rawLockDates.minDate),
  }

  const defaultPeriod = useSelector(insertPeriodSlice.selectors.defaultPeriod)
  const period = useSelector(insertPeriodSlice.selectors.period)
  usePeriod(rawLockDates, period)
  const periodAsUnixTime = useSelector(periodAsUnixTimeSelector)
  const license = useAppSelector(licenseSlice.selectors.license)
  const appMode = useSelector(appSlice.selectors.mode)
  const isKiosk = useAppSelector(isKioskSelector)
  const country = useSelector(appSlice.selectors.country)
  const sectorRules = useSelector(sectorRulesSlice.selectors.rules)
  const bookingAvailability = useAppSelector(
    bookingAvailabilitySlice.selectors.self
  )
  const bookingFlow = useAppSelector(appSlice.selectors.bookingFlow)
  const bookingPeriod = useAppSelector(
    insertPeriodSlice.selectors.bookingPeriod
  )
  const forcedBookingPeriod = useAppSelector(bookingPeriodSelector)
  const seasonalStart = useAppSelector(licenseSlice.selectors.seasonalStart)
  const seasonalEnd = useAppSelector(licenseSlice.selectors.seasonalEnd)
  const areTicketsAvailable = useAppSelector(
    bookingAvailabilitySlice.selectors.areTicketsAvailable
  )
  const areSpotsAvailable = useAppSelector(
    bookingAvailabilitySlice.selectors.areSpotsAvailable
  )
  const groupIds = useAppSelector(servicesSlice.selectors.groupIds)

  const { loadUnavailableSectors } = useLoadUnavailableSectors()

  // local state
  const [showSectorRulesDialog, setShowSectorRulesDialog] =
    React.useState(false)
  const [pageState, setPageState] = useState<
    'loading' | 'loaded' | 'submitted'
  >('loaded')
  const [showPreemptionCodeErrorDialog, setShowPreemptionCodeErrorDialog] =
    useState(false)
  const [preemptionCodeSectorRuleError, setPreemptionCodeSectorRuleError] =
    useState<
      | SectorRuleError.INVALID_END_DATE
      | SectorRuleError.TOO_MANY_RESERVATIONS
      | null
    >(null)

  const isPeriodSoldOut =
    (rd.isSuccess(bookingAvailability) &&
      bookingAvailability.value.type === BookingAvailability.SPOTS &&
      !areSpotsAvailable) ||
    (rd.isSuccess(bookingAvailability) &&
      bookingAvailability.value.type === BookingAvailability.TICKETS &&
      !areTicketsAvailable) ||
    (rd.isSuccess(bookingAvailability) &&
      bookingAvailability.value.type ===
        BookingAvailability.SPOTS_AND_TICKETS &&
      !areTicketsAvailable &&
      !areSpotsAvailable)

  const periodAsDate = useMemo(() => {
    const formattedPeriod = period
      ? {
          end: toJSDateWithTimezone(DateTime.fromISO(period.end)),
          start: toJSDateWithTimezone(DateTime.fromISO(period.start)),
        }
      : null

    return formattedPeriod
  }, [period])

  const defaultPeriodAsDate = useMemo(() => {
    const formattedPeriod = defaultPeriod
      ? {
          end: toJSDateWithTimezone(DateTime.fromISO(defaultPeriod.end)),
          start: toJSDateWithTimezone(DateTime.fromISO(defaultPeriod.start)),
        }
      : null

    return formattedPeriod
  }, [defaultPeriod])

  const showShortcuts = useMemo(() => {
    const now = Date.now()

    return (
      now >= lockDates.minDate.getTime() && now <= lockDates.maxDate.getTime()
    )
  }, [lockDates])

  useEffect(() => {
    if (!periodAsDate) return

    // If the user lands on the page with a period that includes dates that recur
    // before the bookingStartPeriod or after the bookingEndPeriod,
    // we need to change the period to a valid start and end date

    if (
      periodAsDate.start < lockDates.minDate ||
      (periodAsDate.end > lockDates.maxDate &&
        DateTime.fromJSDate(lockDates.minDate) >=
          DateTime.fromFormat(license?.bookingStartPeriod || '', 'dd-LL'))
    ) {
      const formattedMinDate = DateTime.fromJSDate(lockDates.minDate).toFormat(
        'yyyy-LL-dd'
      )

      if (
        formattedMinDate === period?.start ||
        formattedMinDate === period?.end
      ) {
        return
      }

      dispatch(
        insertPeriodSlice.actions.setPeriod({
          end: formattedMinDate,
          start: formattedMinDate,
        })
      )
      dispatch(
        bookingAvailabilitySlice.actions.load({
          bookingFlow,
        })
      )
    }
  }, [periodAsDate, lockDates, license?.bookingStartPeriod])

  useEffect(() => {
    resetSessionStorageData()
  }, [])

  useEffect(() => {
    if (!license) {
      return
    }

    dispatch(sectorRulesSlice.actions.load(license.license))
  }, [license, dispatch])

  useEffect(() => {
    setPageState('loaded')

    if (bookingPeriod === BookingPeriod.ALL_SEASON && periodAsDate) {
      dispatch(insertPeriodSlice.actions.setBookingPeriod(null))
      dispatch(
        insertPeriodSlice.actions.setPeriod({
          end: lockDates.minDate.toISOString(),
          start: lockDates.minDate.toISOString(),
        })
      )
    }
  }, [])

  useEffect(() => {
    if (
      !license ||
      !period ||
      !bookingPeriod ||
      !rd.isSuccess(bookingAvailability) ||
      (rd.isSuccess(bookingAvailability) &&
        bookingAvailability.value.type === BookingAvailability.NONE)
    ) {
      return
    }

    if (
      pageState === 'submitted' &&
      !isPeriodSoldOut &&
      period.start &&
      period.end &&
      license &&
      !showSectorRulesDialog
    ) {
      const nextPath = getNextPathAfterChoosingPeriod({
        bookingAvailability,
        bookingPeriod: forcedBookingPeriod,
        groupIds,
        license,
      })
      if (nextPath) {
        dispatch(
          insertPeriodSlice.actions.setBookingPeriod(forcedBookingPeriod)
        )
        onNavigate(nextPath)
      }
    }
  }, [
    pageState,
    license,
    period,
    bookingAvailability,
    groupIds,
    bookingPeriod,
    isPeriodSoldOut,
  ])

  if (!license || rawLockDates === null || sectorRules === null) {
    return <LoaderTrigger />
  }

  const bookingStartPeriodSplit = license.bookingStartPeriod.split('-')
  const bookingStartDay = parseInt(bookingStartPeriodSplit[0])
  const bookingStartMonth = parseInt(bookingStartPeriodSplit[1])

  const bookingEndPeriodSplit = license.bookingEndPeriod.split('-')
  const bookingEndDay = parseInt(bookingEndPeriodSplit[0])
  const bookingEndMonth = parseInt(bookingEndPeriodSplit[1])

  function onChangeBookingPeriod(value: BookingPeriod): void {
    dispatch(insertPeriodSlice.actions.setBookingPeriod(value))

    if (value == BookingPeriod.ALL_SEASON && seasonalStart && seasonalEnd) {
      const seasonalStartDateTime = DateTime.fromFormat(
        seasonalStart,
        'dd-LL-yyyy'
      )
      const today = DateTime.now()

      const formattedStart =
        seasonalStartDateTime > today
          ? seasonalStartDateTime.toFormat('yyyy-LL-dd')
          : today.toFormat('yyyy-LL-dd')

      const formattedEnd = DateTime.fromFormat(
        seasonalEnd,
        'dd-LL-yyyy'
      ).toFormat('yyyy-LL-dd')

      handleSubmit(
        {
          end: formattedEnd,
          start: formattedStart,
        },
        value
      )
    }
  }

  function onNavigate(url: string) {
    if (license === null || period === null) {
      return
    }
    const isWidget = appMode === AppMode.WIDGET
    const hasIFrameQueryParam = searchParams.get('target') === 'iframe'

    if (!(isWidget && isInIFrame) || (isInIFrame && hasIFrameQueryParam)) {
      navigate({
        pathname: url,
        search: searchParams.toString(),
      })
      return
    }

    searchParams.set('startdate', period.start)
    searchParams.set('enddate', period.end)
    searchParams.set(
      HALF_DAY_QUERY_PARAM,
      String(getHalfDayValueFromBookingPeriod(bookingPeriod))
    )

    const path = `${generatePath(ROOT_PATH, { license: license.license })}?${searchParams}`
    window.open(path, '_parent')
  }

  async function handleSubmit(
    period: PeriodSliceState['period'],
    bookingPeriod: BookingPeriod | null
  ) {
    if (!license) {
      return
    }

    setPageState('loading')

    if (
      !bookingPeriod ||
      (period?.start !== period?.end &&
        bookingPeriod !== BookingPeriod.ALL_SEASON)
    ) {
      dispatch(
        insertPeriodSlice.actions.setBookingPeriod(BookingPeriod.ALL_DAY)
      )
    }

    dispatch(insertPeriodSlice.actions.setPeriod(period))
    await dispatch(
      bookingAvailabilitySlice.actions.load({
        bookingFlow,
      })
    )

    try {
      const shouldValidate = await getShouldValidateForm({
        country,
        license,
        period,
        sectorRules,
      })
      if (typeof shouldValidate !== 'boolean') {
        // something bad happended
        // TODO handle error;
        return
      }

      if (shouldValidate) {
        setShowSectorRulesDialog(true)
      } else {
        loadUnavailableSectors(period).then((payload) => {
          if (
            'errors' in payload &&
            payload.errors.find((error) => {
              const isInvalidEndDate =
                error.ruleType === 'arrival-date' &&
                error.ruleError === SectorRuleError.INVALID_END_DATE
              const isTooManyReservations =
                error.ruleType === 'max-reservations-per-code' &&
                error.ruleError === SectorRuleError.TOO_MANY_RESERVATIONS

              if (isInvalidEndDate) {
                setPreemptionCodeSectorRuleError(
                  SectorRuleError.INVALID_END_DATE
                )
                return true
              }

              if (isTooManyReservations) {
                setPreemptionCodeSectorRuleError(
                  SectorRuleError.TOO_MANY_RESERVATIONS
                )
                return true
              }

              return false
            })
          ) {
            setPageState('loaded')
            setShowPreemptionCodeErrorDialog(true)
          } else {
            setPageState('submitted')
          }
        })
      }
    } catch {
      // TODO: show error
    }
  }

  return (
    <>
      <main
        className={cn(
          'flex flex-grow flex-col lg:items-center lg:justify-center',
          { 'lg:py-12': appMode !== AppMode.WIDGET },
          { 'px-4 pb-12 pt-8': appMode === AppMode.WIDGET }
        )}
      >
        <div
          className={cn('w-full lg:max-w-[50rem]', {
            'mx-auto max-w-xl': appMode === AppMode.WIDGET,
            'px-4 pt-4 lg:mx-auto lg:p-0': appMode !== AppMode.WIDGET,
          })}
        >
          <InsertPeriodAlerts />

          <PreemptionCode />
          <PreemptionCodeErrorDialog
            isOpen={showPreemptionCodeErrorDialog}
            onConfirm={() => {
              dispatch(sectorRulesSlice.actions.setPreemptionCode(null))
              dispatch(appSlice.actions.setSct(null))
              setPageState('submitted')
            }}
            sectorRuleError={preemptionCodeSectorRuleError}
            setIsOpen={setShowPreemptionCodeErrorDialog}
          />
        </div>

        <div
          className={cn(
            'w-full bg-white lg:max-w-[50rem]',
            {
              'flex grow flex-col lg:grow-0 lg:overflow-hidden lg:rounded lg:shadow-[0_2px_10px_0_rgba(0,0,0,.1)]':
                appMode !== AppMode.WIDGET,
            },
            {
              'mx-auto max-w-xl overflow-hidden rounded shadow-[0_2px_10px_0_rgba(0,0,0,.1)]':
                appMode === AppMode.WIDGET,
            }
          )}
        >
          <DatePicker
            defaultPeriod={defaultPeriodAsDate}
            isLoading={pageState === 'loading'}
            isWidget={appMode === AppMode.WIDGET}
            labels={{
              bookingPeriod: {
                afternoon: t('datepicker.bookingPeriod.afternoon'),
                all_day: t('datepicker.bookingPeriod.allDay'),
                all_season: t('datepicker.bookingPeriod.allSeason'),
                morning: t('datepicker.bookingPeriod.morning'),
              },
              confirmBtn: t('datepicker.confirm'),
              day: t('datepicker.day'),
              days: t('datepicker.days'),
              shortcuts: {
                nextWeekend: t('datepicker.shortcuts.nextWeekend'),
                thisWeekend: t('datepicker.shortcuts.thisWeekend'),
                today: t('datepicker.shortcuts.today'),
                tomorrow: t('datepicker.shortcuts.tomorrow'),
              },
            }}
            lang={i18n.language}
            lockDates={lockDates}
            lockPluginFilter={(date) => {
              if (Array.isArray(date)) {
                return false
              }

              const day = date.getDate()
              const month = date.getMonth() + 1

              if (month < bookingStartMonth || month > bookingEndMonth) {
                return true
              }

              if (month === bookingStartMonth && day < bookingStartDay) {
                return true
              }

              if (month === bookingEndMonth && day > bookingEndDay) {
                return true
              }

              return false
            }}
            mode="booking"
            onChangeBookingPeriod={(bookingPeriod) => {
              onChangeBookingPeriod(bookingPeriod)
            }}
            onConfirm={(period) => {
              const storePeriod: PeriodSliceState['period'] = {
                end: DateTime.fromJSDate(period.end).toFormat('yyyy-LL-dd'),
                start: DateTime.fromJSDate(period.start).toFormat('yyyy-LL-dd'),
              }

              handleSubmit(storePeriod, bookingPeriod)
            }}
            period={periodAsDate}
            selectedBookingPeriod={bookingPeriod || BookingPeriod.ALL_DAY}
            settings={{
              bookingHalfDay: license.bookingHalfDay,
              bookingSeasonal: !isKiosk && license.bookingSeasonal,
            }}
            showShortcuts={showShortcuts}
          />
        </div>

        <SectorRulesDialog
          isOpen={showSectorRulesDialog}
          onConfirm={(unavailableSectors) => {
            setShowSectorRulesDialog(false)

            if (!periodAsUnixTime || !bookingPeriod) {
              return
            }

            dispatch(
              sectorRulesSlice.actions.setUnavailableSectors(unavailableSectors)
            )
            setPageState('submitted')
          }}
          setIsOpen={setShowSectorRulesDialog}
        />
      </main>

      <KioskTimeoutDialog timeout={ONE_MINUTE} />
    </>
  )
}
